import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import swal from 'sweetalert2';
import { AppSharedModule } from 'app/app-shared.module';
import { AuthStateService } from 'app/core/services/auth-state.service';
import {
  RemoteSupportConnectFailure,
  RemoteSupportService,
  connectFailureOf,
} from 'app/core/services/remote-support.service';
import {
  REMOTE_SUPPORT_PERMISSIONS,
  RemoteSupportGrant,
} from 'app/shared/models/remote-support.model';
import {
  RemoteSupportSession,
  RemoteSupportSnapshot,
} from './remote-support-session';
import { ScreenRotation } from './screen-rotation';
import {
  DEFAULT_SCREEN_PRESET,
  SCREEN_PRESETS,
  ScreenPreset,
} from './screen-quality';

const I18N_PREFIX = 'remote-support';

/** What the operator reads from the terminal: four letters, then four digits. */
const CODE_LETTERS = 4;
const CODE_DIGITS = 4;
const CODE_LETTER = /[A-Z]/;
const CODE_DIGIT = /[0-9]/;
/** Accepts the code typed with or without its dash, in any case. */
const CODE_INPUT_PATTERN = /^\s*[A-Za-z]{4}[\s-]?[0-9]{4}\s*$/;
const CODE_SEPARATOR = '-';

/** End reasons the contract names; anything else is shown as a generic end. */
const KNOWN_END_REASONS = [
  'code-expired',
  'technician-ended',
  'revoked',
  'replaced',
  'connection-lost',
  'operator',
];

/** Normalises a typed code to the form isoFarm issues: `ABCD-1234`. */
export function normalizeCode(input: string): string {
  const compact = input.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return `${compact.slice(0, CODE_LETTERS)}${CODE_SEPARATOR}${compact.slice(CODE_LETTERS)}`;
}

/**
 * Masks a code as it is typed: up to four letters, the dash, then up to four digits, upper case.
 * Characters out of place (a digit among the letters, a letter among the digits, any symbol) are
 * dropped, and the dash the field shows is written by the mask, so the technician never has to.
 *
 * A dash already in the field marks where the digits start, so erasing or fixing a letter in the
 * middle of `ABCD-2345` keeps the digits (`ACD-2345`) and the pattern blocks the short code until
 * the letter is back. Letters are taken only until the first digit.
 *
 * @param input What the field holds after the last keystroke or paste.
 * @param keepSeparator Whether to show the dash right after the fourth letter, before any digit.
 * The page asks for it while the code grows, so the dash appears as the fourth letter is typed,
 * and not when the technician erases the first digit, so the dash can be erased too.
 */
export function maskCode(input: string, keepSeparator: boolean): string {
  let letters = '';
  let digits = '';
  let pastSeparator = false;
  input
    .toUpperCase()
    .split('')
    .forEach((character) => {
      const digitsStarted = pastSeparator || letters.length === CODE_LETTERS;
      if (character === CODE_SEPARATOR) {
        pastSeparator = true;
      } else if (CODE_LETTER.test(character)) {
        if (letters.length < CODE_LETTERS && !digits) {
          letters += character;
        }
      } else if (
        digitsStarted &&
        digits.length < CODE_DIGITS &&
        CODE_DIGIT.test(character)
      ) {
        digits += character;
      }
    });
  const complete = letters.length === CODE_LETTERS;
  if (digits || (complete && keepSeparator)) {
    return `${letters}${CODE_SEPARATOR}${digits}`;
  }
  return letters;
}

/**
 * Remote support of agroNave terminals for technicians: redeem the code the operator reads from
 * the terminal and see, or operate, its screen over a direct WebRTC connection. isoFarm decides
 * what the account may do on each terminal; the page offers only that.
 */
@Component({
  selector: 'app-remote-support',
  standalone: true,
  imports: [ReactiveFormsModule, AppSharedModule],
  templateUrl: './remote-support.component.html',
  styleUrl: './remote-support.component.scss',
})
export class RemoteSupportComponent implements OnDestroy {
  private readonly remoteSupport = inject(RemoteSupportService);
  private readonly authState = inject(AuthStateService);
  private readonly translate = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  readonly codeControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(CODE_INPUT_PATTERN)],
  });

  /** How the terminal sends its screen; kept across the sessions of the page. */
  readonly screenPresets = SCREEN_PRESETS;
  readonly presetControl = new FormControl<ScreenPreset>(
    DEFAULT_SCREEN_PRESET,
    { nonNullable: true },
  );

  connecting = false;
  connectFailure: RemoteSupportConnectFailure | null = null;
  grant: RemoteSupportGrant | null = null;
  snapshot: RemoteSupportSnapshot | null = null;

  private session: RemoteSupportSession | null = null;
  /** The leave dialog on screen, shared by the navigations that wait on it. */
  private leaveConfirmation: Promise<boolean> | null = null;
  private lastCode = '';
  /** What the code field showed after the last mask, to tell typing from erasing. */
  private maskedCode = '';
  private screenElement: HTMLElement | null = null;
  private rotation: ScreenRotation | null = null;

  @ViewChild('screen')
  set screenRef(ref: ElementRef<HTMLElement> | undefined) {
    const element = ref?.nativeElement ?? null;
    if (element !== this.screenElement) {
      this.rotateScreen(element);
    }
    this.screenElement = element;
    this.session?.attachScreen(this.screenElement);
  }

  @ViewChild('viewer') private viewerRef?: ElementRef<HTMLElement>;
  @ViewChild('codeInput') private codeInputRef?: ElementRef<HTMLInputElement>;

  constructor() {
    this.codeControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((typed) => this.maskCodeInput(typed));
    this.presetControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((preset) => this.session?.setScreenPreset(preset));
  }

  ngOnDestroy(): void {
    this.session?.leave();
    this.rotation?.dispose();
  }

  /**
   * Whether the technician may leave the page (the route's `canDeactivate`). Leaving only closes
   * this tab's channel, and the terminal would keep the session open with no technician on it, so
   * during a session the page asks first and ends the session on confirmation. A signed-out
   * account (logout, expired session) is let go at once, ending the session: it cannot stay here.
   */
  confirmLeave(): boolean | Promise<boolean> {
    if (!this.inSession) {
      return true;
    }
    if (!this.authState.getToken()) {
      this.session?.end();
      return true;
    }
    if (this.leaveConfirmation) {
      return this.leaveConfirmation;
    }
    this.leaveConfirmation = swal
      .fire({
        icon: 'warning',
        title: this.translate.instant(`${I18N_PREFIX}.leave.title`),
        text: this.translate.instant(`${I18N_PREFIX}.leave.message`, {
          serial: this.grant?.serial,
        }),
        confirmButtonText: this.translate.instant(
          `${I18N_PREFIX}.leave.confirm`,
        ),
        cancelButtonText: this.translate.instant(`${I18N_PREFIX}.leave.cancel`),
        showCancelButton: true,
        focusCancel: true,
      })
      .then((result) => {
        if (result.isConfirmed) {
          // No echo is needed: the browser sends end before leave() closes the socket,
          // and the signalling server queues the technician's frames and close in order.
          this.session?.end();
          return true;
        }
        return false;
      })
      .finally(() => {
        this.leaveConfirmation = null;
      });
    return this.leaveConfirmation;
  }

  /** Closing or reloading the tab during a session: the browser asks with its own prompt. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.inSession) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.inSession) {
      // Best effort as the page goes away: queue end before the browser closes the socket.
      this.session?.end();
    }
  }

  connect(): void {
    if (this.connecting) {
      return;
    }
    if (this.codeControl.invalid) {
      this.codeControl.markAsTouched();
      return;
    }
    this.openSession(normalizeCode(this.codeControl.value));
  }

  /** The owner of a session may connect again with the same code while the session lasts. */
  reconnect(): void {
    this.openSession(this.lastCode);
  }

  endSession(): void {
    this.session?.end();
  }

  /** Back to the code form, after a session ended. */
  startOver(): void {
    this.session?.leave();
    this.session = null;
    this.grant = null;
    this.snapshot = null;
    this.connectFailure = null;
    this.codeControl.reset();
  }

  toggleFullscreen(): void {
    const viewer = this.viewerRef?.nativeElement;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else if (viewer) {
      viewer.requestFullscreen().catch(() => undefined);
    }
  }

  get inSession(): boolean {
    return this.snapshot !== null && this.snapshot.phase !== 'ended';
  }

  get ended(): boolean {
    return this.snapshot?.phase === 'ended';
  }

  get canReconnect(): boolean {
    const failure = this.snapshot?.failure;
    return failure === 'ticket-refused' || failure === 'signalling-lost';
  }

  get viewOnly(): boolean {
    return !this.permits(REMOTE_SUPPORT_PERMISSIONS.SCREEN_INPUT);
  }

  /** What the session is doing, before the screen shows. */
  get statusKey(): string {
    const snapshot = this.snapshot;
    if (!snapshot) {
      return '';
    }
    if (snapshot.phase === 'connected') {
      return `${I18N_PREFIX}.screen.${snapshot.screen}`;
    }
    return `${I18N_PREFIX}.phase.${snapshot.phase}`;
  }

  get endKey(): string {
    const snapshot = this.snapshot;
    if (snapshot?.failure) {
      return `${I18N_PREFIX}.failure.${snapshot.failure}`;
    }
    if (snapshot?.endReason && KNOWN_END_REASONS.includes(snapshot.endReason)) {
      return `${I18N_PREFIX}.end.${snapshot.endReason}`;
    }
    return `${I18N_PREFIX}.end.other`;
  }

  presetKey(preset: ScreenPreset): string {
    return `${I18N_PREFIX}.preset.${preset}`;
  }

  get connectFailureKey(): string {
    return `${I18N_PREFIX}.errors.${this.connectFailure}`;
  }

  /**
   * Rewrites what the technician typed into the masked code. The caret goes right after the
   * characters the mask kept from what was before it, so an edit in the middle does not throw it
   * to the end, even when the mask drops a character after the caret.
   */
  private maskCodeInput(typed: string): void {
    const growing = typed.length > this.maskedCode.length;
    const endsWithSeparator = typed.trimEnd().endsWith(CODE_SEPARATOR);
    const masked = maskCode(typed, growing || endsWithSeparator);
    this.maskedCode = masked;
    if (masked === typed) {
      return;
    }
    const input = this.codeInputRef?.nativeElement;
    const typedCaret = input?.selectionEnd ?? typed.length;
    const beforeCaret = typed.slice(0, typedCaret);
    const caretAtEnd = typedCaret >= typed.length;
    const keepSeparatorBeforeCaret =
      beforeCaret.includes(CODE_SEPARATOR) ||
      (caretAtEnd && (growing || endsWithSeparator));
    this.codeControl.setValue(masked, { emitEvent: false });
    if (input && document.activeElement === input) {
      const caret = Math.min(
        masked.length,
        maskCode(beforeCaret, keepSeparatorBeforeCaret).length,
      );
      input.setSelectionRange(caret, caret);
    }
  }

  private permits(permission: string): boolean {
    return this.snapshot?.permissions.includes(permission) ?? false;
  }

  private openSession(code: string): void {
    this.session?.leave();
    this.session = null;
    this.connecting = true;
    this.connectFailure = null;
    this.remoteSupport
      .connect(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (grant) => {
          this.connecting = false;
          this.lastCode = code;
          this.startSession(grant);
        },
        error: (error: unknown) => {
          this.connecting = false;
          if (this.snapshot) {
            this.startOver();
          }
          this.connectFailure = connectFailureOf(error);
        },
      });
  }

  /**
   * Turns the screen element as the terminal draws its screen. The rotation listens to every mouse
   * move over the screen, so it runs outside Angular's zone: it changes nothing the template shows.
   */
  private rotateScreen(element: HTMLElement | null): void {
    this.rotation?.dispose();
    this.rotation = null;
    if (element) {
      const rotation = this.zone.runOutsideAngular(
        () => new ScreenRotation(element),
      );
      rotation.update(this.snapshot?.rotated ?? false);
      this.rotation = rotation;
    }
  }

  private startSession(grant: RemoteSupportGrant): void {
    const session = new RemoteSupportSession(grant);
    session.setScreenPreset(this.presetControl.value);
    this.session = session;
    this.grant = grant;
    // WebRTC callbacks run outside Angular's zone; the page re-renders on each state change.
    session.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((snapshot) => {
        if (session === this.session) {
          this.zone.run(() => {
            this.snapshot = snapshot;
            this.rotation?.update(snapshot.rotated);
          });
        }
      });
    session.start();
    session.attachScreen(this.screenElement);
  }
}
